import type { ReactNode, SVGAttributes } from 'react';

export interface ShellStrokeIconProps extends SVGAttributes<SVGSVGElement> {
  size?: number;
  strokeWidth?: number;
}

/** Lucide-style stroke icon for app shell showcase (no external icon dependency). */
export function ShellStrokeIcon({
  children,
  size = 16,
  strokeWidth = 1.75,
  className,
  ...props
}: ShellStrokeIconProps & { children: ReactNode }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export function ShellIconHome(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M3 10.5 12 3l9 7.5" />
      <path d="M5 9.5V20a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V9.5" />
    </ShellStrokeIcon>
  );
}

export function ShellIconClipboardList(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M8 11h8M8 15h6M8 19h4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCamera(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
      <circle cx="12" cy="13" r="4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCloudUpload(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M12 13v8" />
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <path d="m8 17 4-4 4 4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCalendar(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </ShellStrokeIcon>
  );
}

export function ShellIconUsers(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCar(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M5 17h14M3 11l1.5-5A2 2 0 0 1 6.4 4.5h11.2a2 2 0 0 1 1.9 1.5L21 11M5 17v2a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-2M16 17v2a1 1 0 0 0 1 1h1a1 1 0 0 0 1-1v-2M3 11h18v5a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" />
      <circle cx="7.5" cy="14" r=".75" fill="currentColor" />
      <circle cx="16.5" cy="14" r=".75" fill="currentColor" />
    </ShellStrokeIcon>
  );
}

export function ShellIconPackage(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M16.5 9.4 7.5 4.21M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" />
    </ShellStrokeIcon>
  );
}

export function ShellIconReceipt(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M4 2h16v20l-3-2-3 2-2-2-2 2-3-2-3 2z" />
      <line x1="8" y1="7" x2="16" y2="7" />
      <line x1="8" y1="11" x2="16" y2="11" />
      <line x1="8" y1="15" x2="13" y2="15" />
    </ShellStrokeIcon>
  );
}

export function ShellIconBarChart(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <line x1="12" y1="20" x2="12" y2="10" />
      <line x1="18" y1="20" x2="18" y2="4" />
      <line x1="6" y1="20" x2="6" y2="16" />
    </ShellStrokeIcon>
  );
}

export function ShellIconSettings(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 4.27 16.96l.06-.06A1.65 1.65 0 0 0 4.66 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06A2 2 0 1 1 7.04 4.27l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </ShellStrokeIcon>
  );
}

export function ShellIconBuilding(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M3 21h18M5 21V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v16M9 7h.01M9 11h.01M9 15h.01M15 7h.01M15 11h.01M15 15h.01" />
    </ShellStrokeIcon>
  );
}

export function ShellIconSearch(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </ShellStrokeIcon>
  );
}

export function ShellIconChevronDown(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polyline points="6 9 12 15 18 9" />
    </ShellStrokeIcon>
  );
}

export function ShellIconChevronUp(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polyline points="18 15 12 9 6 15" />
    </ShellStrokeIcon>
  );
}

export function ShellIconAward(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="8" r="7" />
      <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
    </ShellStrokeIcon>
  );
}

export function ShellIconBell(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 0 1-3.46 0" />
    </ShellStrokeIcon>
  );
}

export function ShellIconHelpCircle(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </ShellStrokeIcon>
  );
}

export function ShellIconPlus(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </ShellStrokeIcon>
  );
}

export function ShellIconPause(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </ShellStrokeIcon>
  );
}

export function ShellIconPlay(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polygon points="6 3 20 12 6 21 6 3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconFilter(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconDownload(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconLayout(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </ShellStrokeIcon>
  );
}

export function ShellIconLayers(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </ShellStrokeIcon>
  );
}

export function ShellIconWhatsApp(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M3 21l1.65 -3.8a9 9 0 1 1 3.4 2.9l-5.05 .9" />
      <path d="M9 10a.5 .5 0 0 0 1 0v-1a.5 .5 0 0 0 -1 0v1a5 5 0 0 0 5 5h1a.5 .5 0 0 0 0 -1h-1a.5 .5 0 0 0 0 1" />
    </ShellStrokeIcon>
  );
}

export function ShellIconMoreH(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="1" />
      <circle cx="19" cy="12" r="1" />
      <circle cx="5" cy="12" r="1" />
    </ShellStrokeIcon>
  );
}

export function ShellIconChevronLeft(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polyline points="15 18 9 12 15 6" />
    </ShellStrokeIcon>
  );
}

export function ShellIconChevronRight(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polyline points="9 18 15 12 9 6" />
    </ShellStrokeIcon>
  );
}

export function ShellIconX(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCheck(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polyline points="20 6 9 17 4 12" />
    </ShellStrokeIcon>
  );
}

export function ShellIconEdit(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </ShellStrokeIcon>
  );
}

export function ShellIconInfo(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCheckCircle(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </ShellStrokeIcon>
  );
}

export function ShellIconAlertCircle(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </ShellStrokeIcon>
  );
}

export function ShellIconAlertTriangle(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </ShellStrokeIcon>
  );
}

export function ShellIconTrash(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </ShellStrokeIcon>
  );
}

export function ShellIconSparkles(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCommand(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconSun(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </ShellStrokeIcon>
  );
}

export function ShellIconMoon(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </ShellStrokeIcon>
  );
}

export function ShellIconLock(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconEye(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconEyeOff(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
      <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
      <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
      <line x1="2" x2="22" y1="2" y2="22" />
    </ShellStrokeIcon>
  );
}

export function ShellIconZap(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13.5 10H20a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 10.5 14z" />
    </ShellStrokeIcon>
  );
}

export function ShellIconMenu(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <line x1="4" x2="20" y1="12" y2="12" />
      <line x1="4" x2="20" y1="6" y2="6" />
      <line x1="4" x2="20" y1="18" y2="18" />
    </ShellStrokeIcon>
  );
}

export function ShellIconWrench(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" />
    </ShellStrokeIcon>
  );
}

export function ShellIconActivity(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
    </ShellStrokeIcon>
  );
}

export function ShellIconTag(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
      <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
    </ShellStrokeIcon>
  );
}

export function ShellIconUser(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconHistory(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
      <path d="M12 7v5l4 2" />
    </ShellStrokeIcon>
  );
}

export function ShellIconDollarSign(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <line x1="12" x2="12" y1="2" y2="22" />
      <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
    </ShellStrokeIcon>
  );
}

export function ShellIconMaximize(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M8 3H5a2 2 0 0 0-2 2v3" />
      <path d="M21 8V5a2 2 0 0 0-2-2h-3" />
      <path d="M3 16v3a2 2 0 0 0 2 2h3" />
      <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
    </ShellStrokeIcon>
  );
}

export function ShellIconPhone(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
    </ShellStrokeIcon>
  );
}

export function ShellIconImage(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect width="18" height="18" x="3" y="3" rx="2" ry="2" />
      <circle cx="9" cy="9" r="2" />
      <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21" />
    </ShellStrokeIcon>
  );
}

export function ShellIconPaperclip(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
    </ShellStrokeIcon>
  );
}

export function ShellIconSend(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </ShellStrokeIcon>
  );
}

export function ShellIconSmile(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" x2="9.01" y1="9" y2="9" />
      <line x1="15" x2="15.01" y1="9" y2="9" />
    </ShellStrokeIcon>
  );
}

export function ShellIconStop(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" stroke="none" />
    </ShellStrokeIcon>
  );
}

export function ShellIconMic(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v1a7 7 0 0 1-14 0v-1" />
      <line x1="12" x2="12" y1="19" y2="22" />
      <line x1="8" x2="16" y1="22" y2="22" />
    </ShellStrokeIcon>
  );
}

export function ShellIconArchive(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect width="20" height="5" x="2" y="3" rx="1" />
      <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8" />
      <path d="M10 12h4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconRuler(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M21.3 8.7 8.7 21.3a2.4 2.4 0 0 1-3.4 0L2.7 18.7a2.41 2.41 0 0 1 0-3.4L15.3 2.7a2.4 2.4 0 0 1 3.4 0l2.6 2.6a2.4 2.4 0 0 1 0 3.4Z" />
      <path d="m14.5 12.5 2-2" />
      <path d="m11.5 9.5 2-2" />
      <path d="m8.5 6.5 2-2" />
      <path d="m17.5 15.5 2-2" />
    </ShellStrokeIcon>
  );
}

export function ShellIconCopy(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <rect x="9" y="9" width="13" height="13" rx="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </ShellStrokeIcon>
  );
}

export function ShellIconWarehouse(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-4.2a2 2 0 0 1 1.48 0l8 4.2A2 2 0 0 1 22 8.35Z" />
      <path d="M6 18h12" />
      <path d="M6 14h12" />
      <path d="M6 10h12" />
    </ShellStrokeIcon>
  );
}

export function ShellIconArrowUp(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="m5 12 7-7 7 7" />
      <path d="M12 19V5" />
    </ShellStrokeIcon>
  );
}

export function ShellIconArrowDown(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </ShellStrokeIcon>
  );
}

export function ShellIconArrowRight(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </ShellStrokeIcon>
  );
}

export function ShellIconArrowLeftRight(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M8 3 4 7l4 4" />
      <path d="M4 7h16" />
      <path d="m16 21 4-4-4-4" />
      <path d="M20 17H4" />
    </ShellStrokeIcon>
  );
}

export function ShellIconTruck(props: ShellStrokeIconProps) {
  return (
    <ShellStrokeIcon {...props}>
      <path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" />
      <path d="M15 18h-5" />
      <path d="M14 9h4l3 3v4a2 2 0 0 1-2 2h-1" />
      <circle cx="7" cy="18" r="2" />
      <circle cx="17" cy="18" r="2" />
    </ShellStrokeIcon>
  );
}
